import { Modal } from 'bootstrap';
import DOMPurify from 'dompurify';
import { stickerGuide, stickerSheet } from './stickerCropPreview';

const svgNamespace = 'http://www.w3.org/2000/svg';
function element(name, attributes = {}) {
    const node = document.createElementNS(svgNamespace, name);
    Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
    return node;
}

// Match PrintCropGeometry / Math.Round's midpoint-to-even pixel positioning.
function roundPixel(value) {
    const floor = Math.floor(value);
    return value - floor === 0.5 ? floor + (floor % 2) : Math.round(value);
}

export function positionedPrintCrop(crop, horizontal, vertical, zoom) {
    const width = Math.max(1, roundPixel(crop.width / zoom));
    const height = Math.max(1, roundPixel(crop.height / zoom));
    const rangeX = crop.originalWidth - width, rangeY = crop.originalHeight - height;
    return { width, height, rangeX, rangeY,
        x: roundPixel(rangeX * horizontal / 100), y: roundPixel(rangeY * vertical / 100) };
}

export function initializePrintCropEditor(modal, onSaved, onDraft) {
    const form = modal.querySelector('[data-print-crop-editor]');
    const data = JSON.parse(form.dataset.printCropEditor);
    const crop = data.crop;
    const stage = form.querySelector('[data-crop-stage]');
    const horizontalSlider = form.querySelector('[data-crop-horizontal]');
    const verticalSlider = form.querySelector('[data-crop-vertical]');
    const zoomSlider = form.querySelector('[data-crop-zoom]');
    const zoomValue = form.querySelector('[data-crop-zoom-value]');
    const error = form.querySelector('[data-crop-error]');
    const submit = form.querySelector('[type=submit]');
    let horizontal = data.horizontalOffset;
    let vertical = data.verticalOffset;
    let zoom = data.zoom ?? 1;
    let geometry;
    let saving = false;
    let imageReady = false;
    let drag = null;
    let disposed = false;
    const excessX = crop.originalWidth - crop.width;
    const excessY = crop.originalHeight - crop.height;
    const canvasWidth = crop.originalWidth + excessX;
    const canvasHeight = crop.originalHeight + excessY;
    const svg = element('svg', { viewBox: `0 0 ${canvasWidth} ${canvasHeight}`, 'aria-hidden': 'true' });
    const photo = element('image', { width: crop.originalWidth, height: crop.originalHeight, preserveAspectRatio: 'none' });
    photo.style.filter = data.effect === 'Sepia' ? 'sepia(100%)' : data.effect === 'BlackWhite' ? 'grayscale(100%)' : 'none';
    const edge = element('rect', { class: 'print-crop-original-edge', width: crop.originalWidth, height: crop.originalHeight, 'vector-effect': 'non-scaling-stroke' });
    svg.append(photo, edge);
    // Keep the print frame fixed. The workspace is large enough to show the entire original
    // at both extremes; only the photo and its outer edge move beneath the light masks.
    [
        [0, 0, canvasWidth, excessY],
        [0, excessY + crop.height, canvasWidth, excessY],
        [0, excessY, excessX, crop.height],
        [excessX + crop.width, excessY, excessX, crop.height]
    ].filter(([, , width, height]) => width > 0 && height > 0).forEach(([x, y, width, height]) => {
        svg.append(element('rect', { class: 'print-crop-mask', x, y, width, height }));
    });
    svg.append(element('rect', { class: 'print-crop-frame-contrast', x: excessX, y: excessY,
        width: crop.width, height: crop.height, 'vector-effect': 'non-scaling-stroke' }));
    svg.append(element('rect', { class: 'print-crop-frame', x: excessX, y: excessY,
        width: crop.width, height: crop.height, 'vector-effect': 'non-scaling-stroke' }));
    stage.append(svg);
    if (data.stickerCount > 0) {
        svg.append(stickerGuide(excessX, excessY, crop.width, crop.height, data.stickerInset, data.stickerRadius));
    }

    function showError(message) { error.textContent = message; error.hidden = false; }
    function offset(value) { return Math.max(0, Math.min(100, Math.round(value * 10) / 10)); }
    function draw() {
        const { width, height, rangeX, rangeY } = positionedPrintCrop(crop, horizontal, vertical, zoom);
        horizontal = rangeX ? offset(horizontal) : 50;
        vertical = rangeY ? offset(vertical) : 0;
        const x = roundPixel(rangeX * horizontal / 100), y = roundPixel(rangeY * vertical / 100);
        const scaleX = crop.width / width, scaleY = crop.height / height;
        geometry = { width, height, rangeX, rangeY, x, y, scaleX, scaleY };
        for (const node of [photo, edge]) {
            node.setAttribute('width', crop.originalWidth * scaleX);
            node.setAttribute('height', crop.originalHeight * scaleY);
            node.setAttribute('x', excessX - x * scaleX);
            node.setAttribute('y', excessY - y * scaleY);
        }
        horizontalSlider.value = String(horizontal);
        verticalSlider.value = String(vertical);
        zoomSlider.value = String(zoom);
        zoomValue.textContent = `${Math.round(zoom * 100)} %`;
        horizontalSlider.disabled = saving || !rangeX;
        verticalSlider.disabled = saving || !rangeY;
        const sheet = form.querySelector('[data-sticker-sheet]');
        if (sheet) sheet.replaceChildren(stickerSheet({ url: data.imageUrl, width: crop.originalWidth, height: crop.originalHeight },
            data.effect, { x, y, width, height }, data.stickerCount, data.stickerInset, data.stickerRadius));
    }
    function setZoom(value) {
        const centerX = geometry.x + geometry.width / 2;
        const centerY = geometry.y + geometry.height / 2;
        zoom = Math.max(Number(zoomSlider.min), Math.min(Number(zoomSlider.max), Math.round(value * 100) / 100));
        const width = Math.max(1, roundPixel(crop.width / zoom));
        const height = Math.max(1, roundPixel(crop.height / zoom));
        horizontal = crop.originalWidth > width ? (centerX - width / 2) / (crop.originalWidth - width) * 100 : 50;
        vertical = crop.originalHeight > height ? (centerY - height / 2) / (crop.originalHeight - height) * 100 : 0;
        draw();
    }
    function busy(value) {
        saving = value;
        form.setAttribute('aria-busy', String(value));
        form.querySelectorAll('button, input').forEach(control => { control.disabled = value; });
        submit.disabled = value || !imageReady;
        submit.setAttribute('aria-busy', String(value));
        form.querySelector('[data-crop-spinner]').hidden = !value;
        draw();
    }
    photo.addEventListener('load', () => { imageReady = true; submit.disabled = saving; });
    photo.addEventListener('error', () => { imageReady = false; submit.disabled = true; showError(form.dataset.imageError); });
    photo.setAttribute('href', data.imageUrl);
    draw();
    busy(false);
    horizontalSlider.addEventListener('input', () => { horizontal = Number(horizontalSlider.value); draw(); });
    verticalSlider.addEventListener('input', () => { vertical = Number(verticalSlider.value); draw(); });
    zoomSlider.addEventListener('input', () => setZoom(Number(zoomSlider.value)));
    form.querySelector('[data-crop-zoom-in]').addEventListener('click', () => setZoom(zoom + 0.1));
    form.querySelector('[data-crop-zoom-out]').addEventListener('click', () => setZoom(zoom - 0.1));
    form.querySelector('[data-crop-reset]').addEventListener('click', () => { zoom = 1; horizontal = 50; vertical = data.resetVerticalOffset ?? 0; draw(); });
    stage.addEventListener('pointerdown', event => {
        if (saving || !imageReady || event.button !== 0 || event.isPrimary === false) return;
        const matrix = svg.getScreenCTM();
        if (!matrix) return;
        const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix.inverse());
        drag = { id: event.pointerId, x: point.x, y: point.y, horizontal, vertical };
        stage.setPointerCapture(event.pointerId);
        stage.classList.add('is-dragging');
        event.preventDefault();
    });
    stage.addEventListener('pointermove', event => {
        if (!drag || drag.id !== event.pointerId || saving) return;
        const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(svg.getScreenCTM().inverse());
        if (geometry.rangeX) horizontal = drag.horizontal - (point.x - drag.x) / geometry.scaleX / geometry.rangeX * 100;
        if (geometry.rangeY) vertical = drag.vertical - (point.y - drag.y) / geometry.scaleY / geometry.rangeY * 100;
        draw();
    });
    function endDrag() { drag = null; stage.classList.remove('is-dragging'); }
    stage.addEventListener('lostpointercapture', endDrag);
    stage.addEventListener('pointercancel', endDrag);
    stage.addEventListener('pointerup', endDrag);
    function preventClose(event) { if (saving) event.preventDefault(); }
    function dispose() {
        disposed = true;
        window.removeEventListener('pageshow', restore);
        modal.removeEventListener('hide.bs.modal', preventClose);
        modal.removeEventListener('hidden.bs.modal', dispose);
    }
    modal.addEventListener('hide.bs.modal', preventClose);
    modal.addEventListener('hidden.bs.modal', dispose);
    function restore() { if (!disposed) busy(false); }
    window.addEventListener('pageshow', restore);
    form.addEventListener('submit', async event => {
        event.preventDefault();
        if (saving || !imageReady) return;
        if (onDraft) {
            onDraft({ horizontalOffset: horizontal, verticalOffset: vertical, zoom });
            return;
        }
        const body = new FormData(form);
        body.set('horizontalOffset', String(horizontal));
        body.set('verticalOffset', String(vertical));
        body.set('zoom', String(zoom));
        error.hidden = true;
        busy(true);
        try {
            const response = await fetch(form.action, { method: 'POST', body, credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            if (response.status === 409) {
                const conflict = await response.clone().json().catch(() => null);
                if (conflict?.checkoutReserved && conflict.redirectUrl) {
                    window.location.assign(conflict.redirectUrl);
                    return;
                }
            }
            if (!response.ok || response.redirected) {
                showError(response.status === 409 ? form.dataset.conflict : form.dataset.error);
                return;
            }
            onSaved();
        } catch { showError(form.dataset.error); }
        finally { busy(false); }
    });
    return dispose;
}

export function initializeCartPrintCropping(root = document) {
    const container = root.querySelector('[data-print-crop-modal-container]');
    if (!container || container.dataset.initialized) return;
    container.dataset.initialized = 'true';
    const error = root.querySelector('[data-print-crop-load-error]');
    let loading = false;
    root.addEventListener('click', event => {
        const link = event.target.closest('[data-cart-crop-review]');
        if (!link) return;
        const preview = root.querySelector(link.getAttribute('href'));
        if (!preview) return;
        event.preventDefault();
        preview.scrollIntoView({ block: 'center' });
        preview.focus({ preventScroll: true });
    });
    root.addEventListener('click', async event => {
        const trigger = event.target.closest('[data-print-crop-trigger]');
        if (!trigger) return;
        event.preventDefault();
        if (loading || container.querySelector('.modal')) return;
        loading = true;
        window.ShopActionState.setBusy(trigger, true);
        error.hidden = true;
        try {
            const response = await fetch(trigger.dataset.printCropTrigger, { credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            if (response.status === 409) {
                const conflict = await response.clone().json().catch(() => null);
                if (conflict?.checkoutReserved && conflict.redirectUrl) {
                    window.location.assign(conflict.redirectUrl);
                    return;
                }
            }
            if (!response.ok || response.redirected) throw new Error('Crop editor unavailable');
            container.replaceChildren(DOMPurify.sanitize(await response.text(), { RETURN_DOM_FRAGMENT: true }));
            const modal = container.querySelector('.print-crop-modal');
            if (!modal?.querySelector('[data-print-crop-editor]')) throw new Error('Crop editor missing');
            const instance = new Modal(modal, { backdrop: 'static', keyboard: true });
            initializePrintCropEditor(modal, () => window.location.reload());
            modal.addEventListener('hidden.bs.modal', () => {
                instance.dispose(); container.replaceChildren(); trigger.focus();
            }, { once: true });
            instance.show();
        } catch {
            container.replaceChildren();
            error.hidden = false;
        } finally {
            loading = false; window.ShopActionState.setBusy(trigger, false);
        }
    });
}
